import { EnvironmentProviders, inject, provideAppInitializer } from '@angular/core';
import { LegalConsentService } from '../legal/legal-consent.service';
import { Role } from '../../models/enums';
import { AuthService } from './auth.service';

/**
 * Pre-load de session ("bootstrap bloquant").
 *
 * Angular suspend son bootstrap tant que la promesse retournée par un
 * APP_INITIALIZER n'est pas résolue : le composant racine n'est pas créé et la
 * navigation initiale n'est pas déclenchée. On en profite pour restaurer la session
 * Supabase et charger profil + organisation + abonnement + rôles AVANT que le
 * moindre guard ou composant ne s'exécute.
 *
 * Conséquences :
 *  - les guards deviennent synchrones (`authService.profile` est déjà la vérité) ;
 *  - plus de polling ni de rendu avec des rôles vides ;
 *  - pendant ce temps, l'utilisateur voit le splash statique de `index.html`.
 *
 * `initializeSession()` ne rejette jamais (try/catch + timeout interne) : une panne
 * réseau ne peut pas bloquer le démarrage de l'application.
 *
 * Une fois le profil stable (connecté ou non), on charge aussi l'état RGPD
 * (`LegalConsentService`) : versions en vigueur des documents légaux, puis
 * consentements du profil s'il y en a un. Ces deux appels sont ATTENDUS ici
 * (et non fire-and-forget) pour que le gate RGPD (P4) puisse lire
 * `pendingConsent`/`isBlocking` de façon synchrone dès le premier rendu.
 * Si connecté, on charge aussi `loadHubCatalog()` (documents + versions
 * publiées, avec leur `scope`) — nécessaire pour que `organisationPendingConsent`
 * (P4) sache dès le premier rendu quelles versions en vigueur sont de portée
 * `organisation`, sans attendre une visite de `/documents`.
 * Comme `initializeSession()`, ce bloc ne doit jamais faire échouer le
 * bootstrap : une erreur est loguée et avalée, l'app démarre quand même
 * (ex. hors-ligne, ou legal_documents indisponible).
 */
export const provideSessionBootstrap = (): EnvironmentProviders =>
  provideAppInitializer(async () => {
    // `inject()` doit être appelé de façon synchrone dans le contexte
    // d'injection du callback d'APP_INITIALIZER, donc AVANT tout `await`.
    const authService = inject(AuthService);
    const legalConsentService = inject(LegalConsentService);

    await authService.initializeSession();

    try {
      await legalConsentService.loadCurrentVersions();
      if (authService.profile) {
        await legalConsentService.loadMyConsents();
        await legalConsentService.loadHubCatalog();

        const organisationId = authService.getOrganisationId();
        if (authService.hasRole([Role.SuperAdmin]) && organisationId !== null) {
          await legalConsentService.loadOrganisationConsents(organisationId);
        }
      }
    } catch (error) {
      console.error('Session bootstrap: legal consent preload failed', error);
    }
  });
